import { CloseOutlined } from "@ant-design/icons";
import { Button, Flex, Typography } from "antd";
import React, { useMemo, useEffect, useRef, useState, useContext } from "react";
import { AppContext } from "../../../context/AppContext";
import { mapDataToOption } from "../../utils/CommonVessels";
import { DEFAULT_BUSINESS_DAYS } from "../../../utils/businessHourUtils";
import BusinessHour from "../../../voice/components/Nodes/BusinessHour";
import { ConfigPanelFooter } from "./ConfigPanelFooter";
import HInput from "../../../components/custom/input/input/Input";
import HRadioGroup from "../../../components/custom/input/radio/HRadioGroup";
import HSelect from "../../../components/custom/input/select/Select";
import { countries } from "../../../constants/AppConstants";
import type { ConditionNodeData } from "../nodes/ConditionNode";
import type { WorkflowEnumItem, WorkflowEnumOption } from "../hooks/useWorkflowEnums";
import {
    conditionCodeToTitle,
    conditionCodeToView,
    getConditionIconAndClass,
    type ConditionView,
} from "../utils/conditionCodeMapping";
import { CONDITION_HELP_CONTENT } from "../utils/helpContentConfig";
import { useWorkflowStore } from "../store";
import { selectSelectedStep, selectSelectedStepIndex } from "../store/selectors";

const { Text } = Typography;

const noop = (): void => undefined;

type ConditionViewWithPicker = ConditionView | "picker";

export interface ConditionConfigPanelProps {
    conditionTypes?: WorkflowEnumItem[];
    savedNumbers?: Array<{ phoneNumber?: string; friendlyName?: string; teamName?: string; integId?: string }>;
    scheduleOptions?: WorkflowEnumOption[];
}

const ConditionTypePicker: React.FC<{
    conditionTypes: WorkflowEnumItem[];
    onSelect: (code: string, view: ConditionView) => void;
}> = ({ conditionTypes, onSelect }) => {
    const groups = useMemo(() => {
        const byGroup = new Map<string, WorkflowEnumItem[]>();
        for (const ct of conditionTypes ?? []) {
            const group = ct.meta?.group ?? "Other";
            const arr = byGroup.get(group);
            if (arr) arr.push(ct);
            else byGroup.set(group, [ct]);
        }
        for (const arr of byGroup.values()) {
            arr.sort((a, b) => a.order - b.order);
        }
        return Array.from(byGroup.entries()).map(([label, items]) => ({
            label,
            conditions: items,
        }));
    }, [conditionTypes]);

    return (
        <div className="action-picker">
            {groups.map((group) => (
                <div key={group.label} className="action-picker__group">
                    <div className="hs-color-light-grey-2">{group.label}</div>
                    {group.conditions.map((ct) => {
                        const view = conditionCodeToView(ct.code);
                        const { icon } = getConditionIconAndClass(ct.code);
                        return (
                            <div
                                key={ct.code}
                                className="action-picker__card"
                                onClick={() => onSelect(ct.code, view)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") onSelect(ct.code, view);
                                }}
                            >
                                <div className="action-picker__icon rounded-circle">{icon}</div>
                                <div className="action-picker__text">
                                    <span className="action-picker__title">{ct.idleLabel}</span>
                                    <span className="action-picker__desc">{ct.meta?.description ?? ct.idleLabel}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

const RUN_SCHEDULE_OPTIONS = [
    { value: "24_7", label: "24/7", tag: "Always active" },
    { value: "BusinessHours", label: "Business Hours", tag: "Mon-Fri, 9am-5pm" },
    { value: "OutsideBusinessHours", label: "Outside Business Hours", tag: "Evenings & weekends" },
    { value: "SpecificHours", label: "Specific Hours", tag: "Custom time range" },
];

const KeywordsConditionForm: React.FC<{
    data: ConditionNodeData;
    update: (partial: Partial<ConditionNodeData>) => void;
}> = ({ data, update }) => {
    const [keywordInput, setKeywordInput] = useState("");

    const addKeyword = () => {
        const trimmed = keywordInput.trim().toUpperCase();
        if (!trimmed) return;
        const current = data.keywords ?? [];
        if (current.includes(trimmed)) {
            setKeywordInput("");
            return;
        }
        update({ keywords: [...current, trimmed] });
        setKeywordInput("");
    };

    return (
        <Flex gap="middle" vertical className="px-1 py-3">
            <div>
                <Text className="trigger-rule-config-panel__label hs-fw-500 hs-fs-12 hs-color-BlackRussian">
                    Add Keyword
                </Text>
                <div className="trigger-rule-config-panel__keyword-input-row">
                    <HInput
                        placeholder="e.g. SUPPORT, HELP"
                        value={keywordInput}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeywordInput(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === "Enter" || e.key === ",") {
                                e.preventDefault();
                                addKeyword();
                            }
                        }}
                        className="trigger-rule-config-panel__keyword-input"
                    />
                </div>
                {(data.keywords?.length ?? 0) > 0 && (
                    <div className="trigger-rule-config-panel__keyword-tags">
                        {(data.keywords ?? []).map((kw) => (
                            <span key={kw} className="trigger-rule-config-panel__keyword-tag">
                                {kw}
                                <button
                                    type="button"
                                    className="trigger-rule-config-panel__keyword-tag-remove"
                                    onClick={() =>
                                        update({ keywords: (data.keywords ?? []).filter((k) => k !== kw) })
                                    }
                                    aria-label={`Remove ${kw}`}
                                >
                                    <CloseOutlined />
                                </button>
                            </span>
                        ))}
                    </div>
                )}
                <Text className="trigger-rule-config-panel__hint hs-fs-12 hs-color-mediumDark">
                    Messages containing these words will trigger this flow.
                </Text>
            </div>
            <HRadioGroup
                groupLabel="Match Type"
                options={[
                    { value: "StartsWith", label: "Start With", tag: "Message starts with the keyword." },
                    { value: "Contains", label: "Contains", tag: "Keyword appears anywhere." },
                ]}
                radioGroupProps={{
                    value: data.matchType ?? "Contains",
                    onChange: (e: { target: { value: string } }) => update({ matchType: e.target.value }),
                    className: "trigger-rule-config-panel__radio-group",
                }}
                radioProps={{ className: "trigger-rule-config-panel__radio" }}
            />
        </Flex>
    );
};

type SelectOption = { value: string; label?: string; labelText?: string };

const NumbersConditionForm: React.FC<{
    data: ConditionNodeData;
    update: (partial: Partial<ConditionNodeData>) => void;
    numberOptions: SelectOption[];
}> = ({ data, update, numberOptions }) => (
    <div className="px-1 py-3">
        <Text className="trigger-rule-config-panel__label hs-fw-500 hs-fs-12 hs-color-BlackRussian">
            Numbers
        </Text>
        <HSelect
            bordered={false}
            mode="multiple"
            size="large"
            customProps={{
                value: data.phoneNumbers ?? [],
                onChange: (value: string[]) => update({ phoneNumbers: value }),
                showSearch: false,
            }}
            defaultTagRender={{
                onCloseSetState: (valueOrUpdater: string[] | ((prev: string[]) => string[])) => {
                    const newValue =
                        typeof valueOrUpdater === "function"
                            ? valueOrUpdater(data.phoneNumbers ?? [])
                            : valueOrUpdater;
                    update({ phoneNumbers: newValue });
                },
                tagItemToRender: "label",
            }}
            popupMatchSelectWidth={false}
            placeholder="Select Numbers"
            options={numberOptions}
            loading={false}
        />
        <Text className="trigger-rule-config-panel__hint hs-fs-12 hs-color-mediumDark">
            Limit this rule to messages from selected numbers.
        </Text>
    </div>
);

const CountriesConditionForm: React.FC<{
    data: ConditionNodeData;
    update: (partial: Partial<ConditionNodeData>) => void;
    countryOptions: SelectOption[];
}> = ({ data, update, countryOptions }) => (
    <div className="px-1 py-3">
        <Text className="trigger-rule-config-panel__label hs-fw-500 hs-fs-12 hs-color-BlackRussian">
            Countries
        </Text>
        <HSelect
            mode="multiple"
            placeholder="Select Countries"
            size="large"
            defaultTagRender={{
                onCloseSetState: (valueOrUpdater: string[] | ((prev: string[]) => string[])) => {
                    const newValue =
                        typeof valueOrUpdater === "function"
                            ? valueOrUpdater(data.countries ?? [])
                            : valueOrUpdater;
                    update({ countries: newValue });
                },
            }}
            customProps={{
                value: data.countries ?? [],
                onChange: (val: string[]) => update({ countries: val }),
                className: "w-100",
            }}
            options={countryOptions}
        />
        <Text className="trigger-rule-config-panel__hint hs-fs-12 hs-color-mediumDark">
            Limit this rule to messages from selected countries.
        </Text>
    </div>
);

const ScheduleConditionForm: React.FC<{
    data: ConditionNodeData;
    update: (partial: Partial<ConditionNodeData>) => void;
    scheduleOptions: WorkflowEnumOption[];
    defaultTimeZone: string;
}> = ({ data, update, scheduleOptions, defaultTimeZone }) => {
    const selectedSchedule = data.runSchedule ?? "24_7";
    const scheduleOpts =
        scheduleOptions.length > 0
            ? scheduleOptions.map((o) => ({ value: o.value, label: o.label, tag: o.desc ?? o.label }))
            : RUN_SCHEDULE_OPTIONS;

    const businessHourData = {
        isCalendarHours: false,
        timeZone: data.businessHours?.timeZone ?? defaultTimeZone,
        businessDays: data.businessHours?.businessDays ?? DEFAULT_BUSINESS_DAYS,
    };

    const handleBusinessHourChange = (updater: (prev: typeof businessHourData) => typeof businessHourData) => {
        const next = updater(businessHourData);
        update({
            businessHours: {
                timeZone: next.timeZone,
                businessDays: next.businessDays,
            },
        });
    };

    return (
        <div className="px-1 py-3">
            <HRadioGroup
                options={scheduleOpts}
                radioGroupProps={{
                    value: selectedSchedule,
                    onChange: (e: { target: { value: string } }) => {
                        const val = e.target.value;
                        update({ runSchedule: val });
                        if (val === "SpecificHours" && !data.businessHours) {
                            update({
                                runSchedule: val,
                                businessHours: {
                                    timeZone: businessHourData.timeZone,
                                    businessDays: DEFAULT_BUSINESS_DAYS,
                                },
                            });
                        }
                    },
                    className: "schedule-card-group",
                }}
                radioProps={{ className: "schedule-card" }}
            />
            {selectedSchedule === "SpecificHours" && (
                <div className="trigger-rule-config-panel__business-hours mt-4">
                    <BusinessHour
                        id={undefined}
                        data={undefined}
                        number={undefined}
                        integId={undefined}
                        businessHourSettingLabel={undefined}
                        businessHourData={businessHourData}
                        setBusinessHourData={handleBusinessHourChange}
                        isFromAdminSetting
                    />
                </div>
            )}
        </div>
    );
};

const OtherConditionForm: React.FC = () => (
    <div className="px-1 py-3">
        <Text className="trigger-rule-config-panel__hint hs-fs-12 hs-color-mediumDark">
            Configuration for this condition type is not yet available.
        </Text>
    </div>
);

const getFormForView = (
    view: ConditionView,
    data: ConditionNodeData,
    update: (partial: Partial<ConditionNodeData>) => void,
    options: {
        numberOptions: SelectOption[];
        countryOptions: SelectOption[];
        scheduleOptions: WorkflowEnumOption[];
        defaultTimeZone: string;
    }
): React.ReactNode => {
    switch (view) {
        case "keywords":
            return <KeywordsConditionForm data={data} update={update} />;
        case "numbers":
            return (
                <NumbersConditionForm data={data} update={update} numberOptions={options.numberOptions} />
            );
        case "countries":
            return (
                <CountriesConditionForm data={data} update={update} countryOptions={options.countryOptions} />
            );
        case "schedule":
            return (
                <ScheduleConditionForm
                    data={data}
                    update={update}
                    scheduleOptions={options.scheduleOptions}
                    defaultTimeZone={options.defaultTimeZone}
                />
            );
        case "other":
            return <OtherConditionForm />;
        default:
            return null;
    }
};

const getTitleForView = (view: ConditionView, data: ConditionNodeData): string => {
    switch (view) {
        case "keywords":
            return "Keyword";
        case "numbers":
            return "From Number";
        case "countries":
            return "Countries";
        case "schedule":
            return "Run Schedule";
        case "other":
            return data.title ?? "Condition";
        default:
            return "Condition";
    }
};

export const ConditionConfigPanel: React.FC<ConditionConfigPanelProps> = ({
    conditionTypes = [],
    savedNumbers = [],
    scheduleOptions = [],
}) => {
    const [appProperties] = useContext(AppContext);

    const selectedStep = useWorkflowStore(selectSelectedStep);
    const selectedStepIndex = useWorkflowStore(selectSelectedStepIndex);
    const updateStepData = useWorkflowStore((s) => s.updateStepData);
    const addStep = useWorkflowStore((s) => s.addStep);
    const selectNode = useWorkflowStore((s) => s.selectNode);

    const nodeId = selectedStep?.id ?? "";
    const persistedData = useMemo(
        () => ((selectedStep?.data as ConditionNodeData) ?? {}),
        [selectedStep?.data]
    );
    const [initialData, setInitialData] = useState<ConditionNodeData>(persistedData);
    const [draftData, setDraftData] = useState<ConditionNodeData>(persistedData);

    const [view, setView] = useState<ConditionViewWithPicker>("picker");
    const [showHelp, setShowHelp] = useState(false);

    const numberOptions = useMemo(
        () => mapDataToOption(savedNumbers ?? [], appProperties, noop) ?? [],
        [savedNumbers, appProperties]
    );
    const countryOptions = useMemo(() => countries.map((c) => ({ ...c, labelText: c.label })), []);
    const defaultTimeZone =
        appProperties?.currentUser?.userTimeZone ??
        (typeof Intl !== "undefined" && Intl.DateTimeFormat
            ? Intl.DateTimeFormat().resolvedOptions().timeZone
            : "UTC");
    const prevNodeId = useRef(nodeId);

    useEffect(() => {
        setInitialData(persistedData);
        setDraftData(persistedData);
    }, [nodeId, persistedData]);

    useEffect(() => {
        if (prevNodeId.current !== nodeId) {
            prevNodeId.current = nodeId;
            setView("picker");
            setShowHelp(false);
        }
        if (persistedData.conditionCode) {
            setView(conditionCodeToView(persistedData.conditionCode));
        }
        const hasKeywords = (persistedData.keywords?.length ?? 0) > 0;
        const hasCountries = (persistedData.countries?.length ?? 0) > 0;
        const hasSchedule = !!persistedData.runSchedule;
        const hasNumbers = (persistedData.phoneNumbers?.length ?? 0) > 0;
        if (hasKeywords) setView("keywords");
        else if (hasNumbers) setView("numbers");
        else if (hasCountries) setView("countries");
        else if (hasSchedule) setView("schedule");
    }, [
        nodeId,
        persistedData.conditionCode,
        persistedData.keywords?.length,
        persistedData.countries?.length,
        persistedData.phoneNumbers?.length,
        persistedData.runSchedule,
    ]);

    const handleClose = () => selectNode(null);

    const buildNextDraft = (base: ConditionNodeData, partial: Partial<ConditionNodeData>): ConditionNodeData => {
        const next = { ...base, ...partial };

        const hasConfig =
            (next.phoneNumbers && next.phoneNumbers.length > 0) ||
            (next.keywords && next.keywords.length > 0) ||
            (next.countries && next.countries.length > 0) ||
            !!next.runSchedule;

        if (partial.keywords && partial.keywords.length > 0 && !next.matchType) {
            next.matchType = "Contains";
        }

        const nextConditionCode = next.conditionCode;
        const resolvedTitle = hasConfig
            ? (next.title ?? conditionCodeToTitle(next.conditionCode) ?? "When to continue")
            : (next.title ?? conditionCodeToTitle(nextConditionCode));

        return {
            ...next,
            isPlaceholder: !hasConfig,
            title: resolvedTitle ?? undefined,
        };
    };

    const updateDraft = (partial: Partial<ConditionNodeData>) => {
        setDraftData((prev) => buildNextDraft(prev, partial));
    };

    const commitDraft = () => {
        if (!nodeId) return;
        updateStepData(nodeId, draftData);
        setInitialData(draftData);
    };

    const handleSelectCondition = (code: string, viewType: ConditionView) => {
        const item = conditionTypes.find((ct) => ct.code === code);
        const partial: Partial<ConditionNodeData> = {
            conditionCode: code,
            title: item?.idleLabel ?? "When to continue",
            isPlaceholder: true,
        };
        if (viewType === "schedule") {
            partial.runSchedule = "24_7";
        }
        updateDraft(partial);
        setView(viewType);
    };

    const hasDraftChanges = JSON.stringify(initialData) !== JSON.stringify(draftData);
    const isPlaceholderStep = (persistedData.isPlaceholder ?? false) || !persistedData.conditionCode;
    const canCommit =
        Boolean(draftData.conditionCode) ||
        Boolean(draftData.runSchedule) ||
        (draftData.keywords?.length ?? 0) > 0 ||
        (draftData.phoneNumbers?.length ?? 0) > 0 ||
        (draftData.countries?.length ?? 0) > 0;
    const canAddStep = canCommit;
    const canSaveChanges = hasDraftChanges;

    const handlePrimaryClick = () => {
        if (isPlaceholderStep) {
            if (!canAddStep || selectedStepIndex < 0 || !selectedStep) return;
            commitDraft();
            addStep(selectedStepIndex + 1, selectedStep.type as "condition" | "action");
            return;
        }
        if (!canSaveChanges) return;
        commitDraft();
    };

    if (view === "picker") {
        return (
            <div className="trigger-rule-config-panel">
                <div className="trigger-rule-config-panel__header">
                    <Text className="trigger-rule-config-panel__title hs-fw-600 hs-fs-14 hs-color-BlackRussian">
                        Condition
                    </Text>
                    <Button
                        type="text"
                        className="trigger-rule-config-panel__close"
                        onClick={handleClose}
                        aria-label="Close"
                        icon={<CloseOutlined />}
                    />
                </div>
                <div className="trigger-rule-config-panel__body py-3 px-3">
                    <ConditionTypePicker conditionTypes={conditionTypes} onSelect={handleSelectCondition} />
                </div>
            </div>
        );
    }

    const formOptions = {
        numberOptions,
        countryOptions,
        scheduleOptions,
        defaultTimeZone,
    };

    const helpContent = CONDITION_HELP_CONTENT[view] ?? CONDITION_HELP_CONTENT.other;

    return (
        <div className="trigger-rule-config-panel">
            <div className="trigger-rule-config-panel__header">
                <Flex align="center" gap={8}>
                    <Text className="trigger-rule-config-panel__title hs-fw-600 hs-fs-16 hs-color-BlackRussian">
                        {getTitleForView(view, draftData)}
                    </Text>
                    <div
                        className="automationIconsSprite help-icon cursor-pointer"
                        onClick={() => setShowHelp(!showHelp)}
                        role="button"
                    />
                </Flex>
                <Button
                    type="text"
                    className="trigger-rule-config-panel__close"
                    onClick={handleClose}
                    aria-label="Close"
                    icon={<CloseOutlined />}
                />
            </div>
            <div className="trigger-rule-config-panel__body py-3 px-3">
                {showHelp && (
                    <div className="trigger-rule-config-panel__help-content">
                        <Text className="hs-fs-14 hs-color-mediumDark">
                            How it works
                        </Text>
                        <Text className="hs-fs-12 hs-color-mediumDark d-block mt-1">
                            {helpContent.body}
                        </Text>
                    </div>
                )}
                {getFormForView(view, draftData, updateDraft, formOptions)}
            </div>
            <ConfigPanelFooter
                primaryLabel={isPlaceholderStep ? "Add Step" : "Save Changes"}
                primaryDisabled={isPlaceholderStep ? !canAddStep : !canSaveChanges}
                onPrimaryClick={handlePrimaryClick}
            />
        </div>
    );
};
